import { useQueryClient } from "@tanstack/react-query";
import { useEffect, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router";

import { ROUTES } from "@/app/routes";
import { useSession } from "@/core/auth/session";
import { myBrandsQuery } from "@/core/brand/my-brands";
import { Button } from "@/ui/button";
import { Spinner } from "@/ui/spinner";

export function FullScreen({ children }: { children: ReactNode }) {
  return <div className="flex min-h-dvh items-center justify-center bg-bg p-6">{children}</div>;
}

/**
 * The portal's gate: signed out → sign-in (and back here after). Which brands someone may open is decided
 * per brand (BrandScope); the API enforces the same on every call.
 */
export function RequireSignIn({ children }: { children: ReactNode }) {
  const session = useSession();
  const location = useLocation();
  const queryClient = useQueryClient();
  const checking = session.status === "loading";

  // Every page behind the gate needs the person's brands: ask for them alongside the session check instead
  // of after it, so opening the portal waits for one round trip less.
  useEffect(() => {
    if (checking) void queryClient.prefetchQuery(myBrandsQuery);
  }, [checking, queryClient]);

  if (checking) {
    return (
      <FullScreen>
        <Spinner label="Checking your session…" />
      </FullScreen>
    );
  }
  if (session.status === "unavailable") {
    return (
      <FullScreen>
        <div role="alert" className="flex max-w-sm flex-col items-center gap-4 text-center">
          <p className="text-lg font-bold">Can't reach Brocoly</p>
          <p className="text-sm text-muted">Check your connection. Your work isn't lost.</p>
          <Button onClick={session.retry}>Try again</Button>
        </div>
      </FullScreen>
    );
  }
  if (session.status === "signed-out") {
    const next = location.pathname + location.search;
    const target =
      next === ROUTES.start ? ROUTES.signIn : `${ROUTES.signIn}?next=${encodeURIComponent(next)}`;
    return <Navigate to={target} replace />;
  }
  return children;
}
